import WarningIcon from '@mui/icons-material/Warning'
import { Button, List, ListItem, Typography } from '@mui/material'
import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useNavigate } from 'react-router-dom'

import Dialog, { DialogProps } from 'components/Dialog'
import { CommonFormContextProps, SaveButton } from 'components/Form'
import { AbTest } from 'features/ab-tests/types/abTest'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { ROUTES } from 'routes'

export const ConfirmationDialog = ({
  activeAbTestId,
  formProps,
  isOpen,
  setOpen,
}: Pick<DialogProps, 'isOpen' | 'setOpen'> & {
  activeAbTestId: AbTest['id']
  formProps: CommonFormContextProps
}): JSX.Element => {
  const { workspaceId } = useWorkspaceParam()
  const { t: tC } = useTranslation('common')
  const navigate = useNavigate()
  const { t } = useTranslation('features/abTests', { keyPrefix: 'confirmationDialog' })

  const BulletPointListItem = ({ children }: PropsWithChildren) => (
    <ListItem
      disablePadding
      sx={{ display: 'list-item', pl: 2 }}
    >
      {children}
    </ListItem>
  )

  const Content: JSX.Element = (
    <>
      <Typography>{t('description.line1')}</Typography>

      <List
        dense
        disablePadding
        sx={{
          listStyleType: 'disc',
          ml: 2,
        }}
      />
      <BulletPointListItem>{t('description.line2')}</BulletPointListItem>
      <BulletPointListItem>{t('description.line3')}</BulletPointListItem>
    </>
  )

  const Actions: JSX.Element = (
    <>
      <Button
        color='info'
        data-cy='go-to-ab-test-button'
        onClick={(): void =>
          navigate(
            generatePath(ROUTES.AB_TESTS.EDIT, {
              id: activeAbTestId,
              workspaceId,
            }),
          )
        }
        variant='text'
      >
        {t('actions.goToTest')}
      </Button>

      <SaveButton
        {...formProps}
        onConfirmation={() => void setOpen(false)}
        text={tC('actions.confirm')}
      />
    </>
  )

  return (
    <Dialog
      actions={Actions}
      content={Content}
      isOpen={isOpen}
      setOpen={setOpen}
      title={
        <>
          <WarningIcon
            color='warning'
            data-cy='warning-icon'
          />
          {t('title')}
        </>
      }
    />
  )
}
